// Who's playing with whom, worded from the viewer's seat. Pure, so it's unit-tested.

import { MOON_BID, cardName, type GameView } from '../engine';

const SUIT_GLYPH = { C: '♣', D: '♦', H: '♥', S: '♠' } as const;

/** "Bob and Cy", "Bob, Cy and Di". */
export function listNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function bidText(view: GameView): string {
  const h = view.hand;
  const glyph = h.trump ? ` ${SUIT_GLYPH[h.trump]}` : '';
  return h.bid === MOON_BID ? `the moon${glyph}` : `${h.bid}${glyph}`;
}

/** Points the bidding side must take (the moon needs all 13). */
function need(view: GameView): number {
  return Math.min(view.hand.bid ?? 0, 13);
}

/** Is `i` on the bidder's side? Unknown until the partner is announced. */
export function onBiddingSide(view: GameView, i: number): boolean | null {
  const h = view.hand;
  if (h.bidder === null || h.partner === 'unknown') return null;
  return i === h.bidder || i === h.partner;
}

/** For seat tags: is `i` with the viewer or against them? Null before sides are known, or for yourself. */
export function sideOf(view: GameView, viewer: number | null, i: number): 'with' | 'against' | null {
  if (viewer === null || i === viewer) return null;
  const mine = onBiddingSide(view, viewer);
  const theirs = onBiddingSide(view, i);
  if (mine === null || theirs === null) return null;
  return mine === theirs ? 'with' : 'against';
}

/** "Bidder", "Ann's partner", or nothing. */
export function roleTag(view: GameView, i: number, names: readonly string[], viewer: number | null): string | null {
  const h = view.hand;
  if (h.bidder === null) return null;
  if (i === h.bidder) return 'Bidder';
  if (h.partner !== 'unknown' && h.partner === i) {
    return h.bidder === viewer ? 'Your partner' : `${names[h.bidder]}'s partner`;
  }
  return null;
}

/** The line at the top of your panel saying which side you're on and what you need. */
export function sideLine(view: GameView, seat: number | null, names: readonly string[]): string | null {
  const h = view.hand;
  const p = view.phase.kind;
  if (h.bidder === null || p === 'bid' || p === 'gameOver') return null;
  const bidder = names[h.bidder]!;
  const n = view.config.playerCount;
  const others = (exclude: number[]) => Array.from({ length: n }, (_, i) => i).filter((i) => !exclude.includes(i));
  if (h.partner === 'unknown') {
    return seat === h.bidder ? null : `${bidder} won the bid with ${bidText(view)} and is choosing a partner.`;
  }
  if (seat === null) {
    const side = h.partner === null ? bidder : `${bidder} and ${names[h.partner]}`;
    return `${side} need ${need(view)} of 13.`;
  }
  if (seat === h.bidder) {
    return h.partner === null
      ? `You're playing alone. Take ${need(view)} points.`
      : `You bid ${bidText(view)} with ${names[h.partner]} as your partner. Take ${need(view)} points together.`;
  }
  if (seat === h.partner) return `You're ${bidder}'s partner. Help ${bidder} take ${need(view)} points.`;
  const side = h.partner === null ? bidder : `${bidder} and ${names[h.partner]}`;
  const team = others([h.bidder, ...(h.partner === null ? [] : [h.partner]), seat]).map((i) => names[i]!);
  const with_ = team.length > 0 ? `You're defending with ${listNames(team)}. ` : "You're defending. ";
  return `${with_}Stop ${side} from taking ${need(view)}; you share every point you take.`;
}

/** The felt's plain-words summary of both sides once the partner is known. */
export function sidesSummary(view: GameView, seat: number | null, names: readonly string[]): string | null {
  const h = view.hand;
  if (h.bidder === null || h.partner === 'unknown' || !h.trump) return null;
  const who = (i: number) => (i === seat ? 'you' : names[i]!);
  const bidding = h.partner === null ? [h.bidder] : [h.bidder, h.partner];
  const defenders = Array.from({ length: view.config.playerCount }, (_, i) => i).filter((i) => !bidding.includes(i));
  const lead = bidding.map(who).join(' + ');
  const cap = lead.charAt(0).toUpperCase() + lead.slice(1);
  return `${cap} need ${need(view)} ${SUIT_GLYPH[h.trump]} · defenders: ${defenders.map(who).join(', ')}`;
}

/** Whether the called card is still in someone's hand (not yet on the table). */
export function calledPending(view: GameView): boolean {
  const h = view.hand;
  if (!h.called) return false;
  const shown = h.won.flat().includes(h.called) || (h.trick?.plays.some((p) => p.card === h.called) ?? false);
  return !shown;
}

/** The notice for whoever holds the called card, while they still have to play it. */
export function calledNotice(view: GameView, seat: number | null, names: readonly string[]): string | null {
  const h = view.hand;
  if (seat === null || !h.called || h.bidder === null || !calledPending(view)) return null;
  if (!(h.hands[seat] ?? []).includes(h.called)) return null;
  const card = cardName(h.called);
  if (seat === h.bidder) return `You have the ${card} yourself, so you're playing alone. You must lead it.`;
  const bidder = names[h.bidder]!;
  return `You have the ${card}, which ${bidder} called, so you're ${bidder}'s partner. You must play it.`;
}

